/**
 * Route Constants
 *
 * @version 1.0
 * @module core/modules/waste/constants
 *
 * @description
 * Constantes para rutas de recolección de residuos.
 * Incluye colores, propiedades por defecto, configuración de mapas y utilities.
 *
 * @remarks
 * **NO HARDCODEAR valores en componentes**.
 * Siempre importar desde este archivo para mantener consistencia.
 */

import type { RouteStatus, CollectionFrequency, DayOfWeek } from '../types';

// ============================================================================
// ROUTE STATUS CONSTANTS
// ============================================================================

/**
 * Estados de ruta con labels y colores
 *
 * @remarks
 * Colores sincronizados con routes-layer.ts para visualización en mapa.
 */
export const ROUTE_STATUSES: { value: RouteStatus; label: string; color: string }[] = [
  { value: 'active', label: 'Activa', color: '#2FA4B5' },
  { value: 'paused', label: 'Pausada', color: '#F59E0B' },
  { value: 'discontinued', label: 'Discontinuada', color: '#6B7280' },
];

/**
 * Solo los colores (para usar en map layers)
 *
 * @example
 * ```typescript
 * import { ROUTE_COLORS } from '@waste/constants';
 *
 * const lineColor = ROUTE_COLORS[route.status];
 * ```
 */
export const ROUTE_COLORS: Record<RouteStatus, string> = {
  active: '#2FA4B5',
  paused: '#F59E0B',
  discontinued: '#6B7280',
} as const;

/**
 * Obtener color por status
 */
export function getRouteColor(status: RouteStatus): string {
  return ROUTE_COLORS[status];
}

/**
 * Obtener label por status
 */
export function getRouteStatusLabel(status: RouteStatus): string {
  const found = ROUTE_STATUSES.find((s) => s.value === status);
  return found ? found.label : status;
}

// ============================================================================
// ROUTE PROPERTIES - DEFAULT VALUES
// ============================================================================

/**
 * Propiedades por defecto para rutas nuevas
 *
 * @remarks
 * Usar al crear rutas con propiedades parciales.
 *
 * @example
 * ```typescript
 * import { DEFAULT_ROUTE_PROPS } from '@waste/constants';
 *
 * const newRoute = {
 *   ...userInput,
 *   ...DEFAULT_ROUTE_PROPS,
 * };
 * ```
 */
export const DEFAULT_ROUTE_PROPS = {
  frequency: 'daily' as CollectionFrequency,
  operatingDays: ['MON', 'TUE', 'WED', 'THU', 'FRI'] as DayOfWeek[],
  startTime: '06:00',
  status: 'active' as RouteStatus,
  estimatedDuration: 0,
  estimatedDistance: 0,
  containerIds: [] as string[],
} as const;

/**
 * Hora de inicio por defecto para rutas
 */
export const DEFAULT_ROUTE_START_TIME = '06:00';

/**
 * Frecuencias por defecto más comunes
 */
export const DEFAULT_FREQUENCY: CollectionFrequency = 'daily';

/**
 * Días por defecto (Lun-Vie)
 */
export const DEFAULT_OPERATING_DAYS: DayOfWeek[] = ['MON', 'TUE', 'WED', 'THU', 'FRI'];

// ============================================================================
// ROUTE FREQUENCY HELPERS
// ============================================================================

/**
 * Obtener días operacionales según frecuencia
 *
 * @remarks
 * Utility para auto-rellenar operatingDays al seleccionar frequency.
 *
 * @example
 * ```typescript
 * import { getOperatingDaysForFrequency } from '@waste/constants';
 *
 * const days = getOperatingDaysForFrequency('weekly_mwf');
 * // => ['MON', 'WED', 'FRI']
 * ```
 */
export function getOperatingDaysForFrequency(frequency: CollectionFrequency): DayOfWeek[] {
  switch (frequency) {
    case 'daily':
      return ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
    case 'weekly_mwf':
      return ['MON', 'WED', 'FRI'];
    case 'weekly_tts':
      return ['TUE', 'THU', 'SAT'];
    case 'weekly_once':
      return ['MON']; // Default, usuario puede cambiar
    case 'biweekly':
      return ['MON']; // Default, usuario puede cambiar
    case 'monthly':
      return ['MON']; // Default, primer lunes del mes
    default:
      return DEFAULT_OPERATING_DAYS;
  }
}

/**
 * Obtener color para frecuencia (para badges)
 */
export function getFrequencyColor(frequency: CollectionFrequency): string {
  switch (frequency) {
    case 'daily':
      return '#ef4444'; // rojo (alta frecuencia)
    case 'weekly_mwf':
    case 'weekly_tts':
      return '#f59e0b'; // naranja (media-alta)
    case 'weekly_once':
      return '#3b82f6'; // azul (media)
    case 'biweekly':
      return '#8b5cf6'; // púrpura (baja)
    case 'monthly':
      return '#6b7280'; // gris (muy baja)
    default:
      return '#6b7280';
  }
}

// ============================================================================
// CODE GENERATION
// ============================================================================

/**
 * Prefijo para códigos de ruta
 */
export const ROUTE_CODE_PREFIX = 'RT';

/**
 * Generar código de ruta automático
 *
 * @param existingCodes - Códigos ya existentes (para evitar duplicados)
 * @param prefix - Prefijo personalizado (default: 'RT')
 * @returns Código único en formato RT-001, RT-002, etc.
 *
 * @example
 * ```typescript
 * import { generateRouteCode } from '@waste/constants';
 *
 * const existingCodes = ['RT-001', 'RT-002', 'RT-005'];
 * const newCode = generateRouteCode(existingCodes);
 * // => 'RT-003'
 * ```
 */
export function generateRouteCode(existingCodes: string[], prefix = ROUTE_CODE_PREFIX): string {
  // Extraer números de códigos existentes con el mismo prefijo
  const numbers = existingCodes
    .filter((code) => code.startsWith(`${prefix}-`))
    .map((code) => {
      const match = code.match(/\d+$/);
      return match ? parseInt(match[0], 10) : 0;
    })
    .filter((n) => !isNaN(n));

  // Encontrar el siguiente número disponible
  const maxNumber = numbers.length > 0 ? Math.max(...numbers) : 0;
  const nextNumber = maxNumber + 1;

  // Formatear con padding (001, 002, etc.)
  return `${prefix}-${nextNumber.toString().padStart(3, '0')}`;
}

// ============================================================================
// MAP LAYER CONFIGURATION
// ============================================================================

/**
 * Configuración de líneas de ruta en mapa
 */
export const ROUTE_LINE_STYLES = {
  width: {
    default: 3,
    selected: 5,
    hovered: 4,
  },
  opacity: {
    default: 0.8,
    selected: 1.0,
    hovered: 0.9,
  },
} as const;

/**
 * Configuración de flechas direccionales
 */
export const ROUTE_ARROW_STYLES = {
  symbol: '→',
  size: 16,
  spacing: 100, // pixels entre flechas
  opacity: 0.7,
} as const;

/**
 * Configuración de labels de ruta
 */
export const ROUTE_LABEL_STYLES = {
  textSize: 11,
  textColor: '#FFFFFF',
  textHaloColor: '#000000',
  textHaloWidth: 1.5,
  minZoom: 13, // Solo mostrar labels a partir de este zoom
} as const;

/**
 * IDs de layers en MapLibre (sincronizar con routes-layer.ts)
 */
export const ROUTE_LAYER_IDS = {
  source: 'waste-routes-source',
  lines: 'waste-routes-lines',
  arrows: 'waste-routes-arrows',
  labels: 'waste-routes-labels',
} as const;

// ============================================================================
// OPTIMIZATION CONFIGURATION
// ============================================================================

/**
 * Configuración para optimización de rutas
 */
export const ROUTE_OPTIMIZATION_CONFIG = {
  /**
   * Mínimo de contenedores para poder optimizar
   */
  minContainersToOptimize: 3,

  /**
   * Mejora mínima (en km) para considerar la optimización exitosa
   */
  minImprovementThreshold: 0.01, // 10 metros

  /**
   * Timeout para llamadas al backend TSP (ms)
   */
  tspTimeout: 30000, // 30 segundos

  /**
   * Mostrar mejora como porcentaje si es mayor a este valor
   */
  significantImprovementPercent: 5, // 5%
} as const;

// ============================================================================
// VALIDATION RULES
// ============================================================================

/**
 * Reglas de validación para rutas
 */
export const ROUTE_VALIDATION = {
  /**
   * Mínimo de puntos para una ruta válida
   */
  minLinePoints: 2,

  /**
   * Máximo de puntos (para evitar geometrías muy complejas)
   */
  maxLinePoints: 500,

  /**
   * Longitud mínima de nombre
   */
  minNameLength: 3,

  /**
   * Longitud máxima de nombre
   */
  maxNameLength: 100,

  /**
   * Longitud máxima de código
   */
  maxCodeLength: 20,

  /**
   * Máximo de contenedores por ruta
   */
  maxContainersPerRoute: 200,

  /**
   * Distancia máxima (km) para considerar ruta válida
   */
  maxRouteDistance: 100, // 100 km
} as const;

/**
 * Validar si una ruta puede ser optimizada
 */
export function canOptimizeRoute(containerCount: number): boolean {
  return containerCount >= ROUTE_OPTIMIZATION_CONFIG.minContainersToOptimize;
}

/**
 * Validar si un código de ruta es válido
 */
export function isValidRouteCode(code: string): boolean {
  return code.length > 0 && code.length <= ROUTE_VALIDATION.maxCodeLength;
}

/**
 * Validar si un nombre de ruta es válido
 */
export function isValidRouteName(name: string): boolean {
  return name.length >= ROUTE_VALIDATION.minNameLength && name.length <= ROUTE_VALIDATION.maxNameLength;
}

// ============================================================================
// UI MESSAGES
// ============================================================================

/**
 * Mensajes de UI para rutas
 */
export const ROUTE_MESSAGES = {
  emptyState: 'No hay rutas registradas',
  emptyStateAction: 'Crear primera ruta',
  created: 'Ruta creada exitosamente',
  updated: 'Ruta actualizada',
  deleted: 'Ruta eliminada',
  deleteConfirm: '¿Eliminar esta ruta? Esta acción no se puede deshacer.',
  optimizing: 'Optimizando ruta...',
  optimized: 'Ruta optimizada exitosamente',
  optimizationFailed: 'No se pudo optimizar la ruta',
  noImprovementFound: 'La ruta ya está optimizada',
  invalidGeometry: 'La geometría de la ruta es inválida',
  tooFewContainers: 'Se necesitan al menos 3 contenedores para optimizar',
  loadingRoutes: 'Cargando rutas...',
} as const;

// ============================================================================
// FORMATTING UTILITIES
// ============================================================================

/**
 * @remarks
 * Las funciones de formateo (distancia, duración, mejora de optimización)
 * están en `route-geometry-utils.ts`:
 * - formatDistance(km): string
 * - formatDuration(minutes): string
 * - calculateOptimizationImprovement(original, optimized)
 *
 * Importar desde utils en lugar de duplicar aquí.
 */
